import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

function OrderForm() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const isEditMode = !!orderId;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    status: 'pending',
    payment_status: 'pending',
    payment_method: '',
    currency: 'USD',
    shipping_address: '',
    billing_address: '',
    notes: '',
    items: []
  });

  useEffect(() => {
    loadCustomers();
    loadProducts();
    loadVatRates();
    if (isEditMode) {
      loadOrder();
    } else {
      addItem();
    }
  }, [orderId]);

  const loadCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data.customers || []);
    } catch (err) {
      console.error('Failed to load customers:', err);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await api.get('/ecommerce/products');
      setProducts(response.data.products || []);
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  };

  const loadVatRates = async () => {
    try {
      const response = await api.get('/vat-rates?is_active=true');
      setVatRates(response.data.vat_rates || []);
    } catch (err) {
      console.error('Failed to load VAT rates:', err);
    }
  };

  const loadOrder = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/orders/${orderId}`);
      setFormData({
        customer_id: response.data.customer_id || '',
        status: response.data.status || 'pending',
        payment_status: response.data.payment_status || 'pending',
        payment_method: response.data.payment_method || '',
        currency: response.data.currency || 'USD',
        shipping_address: response.data.shipping_address || '',
        billing_address: response.data.billing_address || '',
        notes: response.data.notes || '',
        items: response.data.items || []
      });
    } catch (err) {
      console.error('Failed to load order:', err);
      setError('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          product_id: '',
          product_name: '',
          quantity: 1,
          unit_price: 0,
          discount: 0,
          vat_rate_id: '',
          vat_amount: 0
        }
      ]
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        [field]: value
      };

      if (field === 'product_id' && value) {
        const product = products.find(p => p.id === parseInt(value));
        if (product) {
          newItems[index].product_name = product.name;
          newItems[index].unit_price = product.base_price || product.price || 0;
          // Auto-select product's VAT rate if available
          if (product.vat_rate_id) {
            newItems[index].vat_rate_id = product.vat_rate_id;
          }
        }
      }

      // Recalculate VAT amount when relevant fields change
      if (['quantity', 'unit_price', 'discount', 'vat_rate_id'].includes(field)) {
        const item = newItems[index];
        const subtotal = (parseFloat(item.unit_price || 0) * parseInt(item.quantity || 1)) - parseFloat(item.discount || 0);
        
        if (item.vat_rate_id) {
          const vatRate = vatRates.find(vr => vr.id === parseInt(item.vat_rate_id));
          if (vatRate) {
            newItems[index].vat_amount = (subtotal * parseFloat(vatRate.rate)) / 100;
          }
        } else {
          newItems[index].vat_amount = 0;
        }
      }

      return {
        ...prev,
        items: newItems
      };
    });
  };

  const calculateItemTotal = (item) => {
    const subtotal = (parseFloat(item.unit_price || 0) * parseInt(item.quantity || 1)) - parseFloat(item.discount || 0);
    const vat = parseFloat(item.vat_amount || 0);
    return subtotal + vat;
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => {
      const subtotal = (parseFloat(item.unit_price || 0) * parseInt(item.quantity || 1)) - parseFloat(item.discount || 0);
      return sum + subtotal;
    }, 0);
  };

  const calculateTotalVat = () => {
    return formData.items.reduce((sum, item) => sum + parseFloat(item.vat_amount || 0), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTotalVat();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.items.length === 0) {
      setError('Please add at least one item');
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (isEditMode) {
        await api.put(`/orders/${orderId}`, formData);
      } else {
        await api.post('/orders', formData);
      }

      navigate('/orders');
    } catch (err) {
      console.error('Failed to save order:', err);
      setError(err.response?.data?.error || 'Failed to save order');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this order?')) {
      return;
    }

    try {
      await api.delete(`/orders/${orderId}`);
      navigate('/orders');
    } catch (err) {
      console.error('Failed to delete order:', err);
      setError('Failed to delete order');
    }
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ marginBottom: '1.5rem', fontSize: '0.875rem' }}>
        <span 
          onClick={() => navigate('/dashboard')} 
          style={{ color: 'var(--gray-500)', cursor: 'pointer' }}
        >
          Dashboard
        </span>
        <span className="breadcrumb-separator">/</span>
        <span 
          onClick={() => navigate('/orders')} 
          style={{ color: 'var(--gray-500)', cursor: 'pointer' }}
        >
          Orders
        </span>
        <span className="breadcrumb-separator">/</span>
        <span style={{ color: 'var(--gray-900)' }}>
          {isEditMode ? 'Edit Order' : 'New Order'}
        </span>
      </div>

      <div className="card">
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '1.5rem', 
          paddingBottom: '1.5rem', 
          borderBottom: '3px solid #EF4444' 
        }}>
          <div>
            <h2 style={{ margin: 0, color: 'var(--gray-900)', fontSize: '1.5rem', fontWeight: '600' }}>
              {isEditMode ? 'Edit Order' : 'Create New Order'}
            </h2>
            <p style={{ margin: '0.25rem 0 0 0', color: 'var(--gray-600)', fontSize: '0.875rem' }}>
              {isEditMode ? 'Update order details and items' : 'Fill in the order information and add items'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {isEditMode && (
              <button
                type="button"
                onClick={handleDelete}
                className="btn btn-danger"
              >
                🗑️ Delete
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/orders')}
              className="btn btn-outline"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
              onClick={handleSubmit}
            >
              {loading ? '⏳ Saving...' : isEditMode ? '💾 Update' : '✨ Create'}
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div style={{ 
              backgroundColor: 'var(--red-50)', 
              border: '1px solid var(--red-200)', 
              borderRadius: '6px', 
              padding: '1rem', 
              marginBottom: '1.5rem' 
            }}>
              <p style={{ margin: 0, color: 'var(--red-800)', fontSize: '0.875rem' }}>{error}</p>
            </div>
          )}

          {/* Basic Information Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ 
              margin: '0 0 1rem 0', 
              color: 'var(--gray-900)', 
              fontSize: '1.125rem', 
              fontWeight: '600',
              paddingBottom: '0.5rem',
              borderBottom: '2px solid var(--gray-200)'
            }}>
              📋 Basic Information
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Customer *
                </label>
                <select
                  id="customer_id"
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleChange}
                  required
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px' }}
                >
                  <option value="">Select a customer</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.first_name} {customer.last_name} ({customer.email})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Currency
                </label>
                <select
                  id="currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px' }}
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Order Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px' }}
                >
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Payment Status
                </label>
                <select
                  id="payment_status"
                  name="payment_status"
                  value={formData.payment_status}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px' }}
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Payment Method
                </label>
                <input
                  type="text"
                  id="payment_method"
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleChange}
                  placeholder="e.g., Credit Card, PayPal"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px' }}
                />
              </div>
            </div>
          </div>

          {/* Order Items Section */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '1rem',
              paddingBottom: '0.5rem',
              borderBottom: '2px solid var(--gray-200)'
            }}>
              <h3 style={{ margin: 0, color: 'var(--gray-900)', fontSize: '1.125rem', fontWeight: '600' }}>
                🛒 Order Items
              </h3>
              <button
                type="button"
                onClick={addItem}
                className="btn btn-sm btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <span>➕</span>
                Add Item
              </button>
            </div>

            {formData.items.map((item, index) => (
              <div key={index} style={{ 
                border: '1px solid var(--gray-200)', 
                borderRadius: '8px', 
                padding: '1rem', 
                marginBottom: '1rem',
                backgroundColor: 'var(--gray-50)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>
                    Item {index + 1}
                  </h4>
                  {formData.items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="btn btn-sm btn-danger"
                    >
                      ✕
                    </button>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem' }}>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                      Product
                    </label>
                    <select
                      value={item.product_id}
                      onChange={(e) => handleItemChange(index, 'product_id', e.target.value)}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.875rem' }}
                    >
                      <option value="">Select product</option>
                      {products.map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name} - ${product.base_price || product.price || 0}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                      Quantity
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.875rem' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                      Unit Price
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.unit_price}
                      onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.875rem' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                      Discount
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.discount}
                      onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.875rem' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                      VAT Rate
                    </label>
                    <select
                      value={item.vat_rate_id || ''}
                      onChange={(e) => handleItemChange(index, 'vat_rate_id', e.target.value)}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.875rem' }}
                    >
                      <option value="">No VAT</option>
                      {vatRates.map(rate => (
                        <option key={rate.id} value={rate.id}>
                          {rate.name} ({rate.rate}%)
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                      VAT Amount
                    </label>
                    <input
                      type="text"
                      value={`$${(item.vat_amount || 0).toFixed(2)}`}
                      disabled
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.875rem', backgroundColor: 'var(--gray-100)' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                      Total
                    </label>
                    <input
                      type="text"
                      value={`$${calculateItemTotal(item).toFixed(2)}`}
                      disabled
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.875rem', backgroundColor: 'var(--gray-100)' }}
                    />
                  </div>
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '1rem', marginTop: '1rem', borderTop: '2px solid var(--gray-200)' }}>
              <div style={{ textAlign: 'right', minWidth: '250px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--gray-200)' }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Subtotal:</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-900)' }}>
                    ${calculateSubtotal().toFixed(2)} {formData.currency}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--gray-200)' }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>VAT:</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-900)' }}>
                    ${calculateTotalVat().toFixed(2)} {formData.currency}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem' }}>
                  <span style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--gray-700)' }}>Total:</span>
                  <span style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--gray-900)' }}>
                    ${calculateTotal().toFixed(2)} {formData.currency}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Addresses Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ 
              margin: '0 0 1rem 0', 
              color: 'var(--gray-900)', 
              fontSize: '1.125rem', 
              fontWeight: '600',
              paddingBottom: '0.5rem',
              borderBottom: '2px solid var(--gray-200)'
            }}>
              📍 Addresses
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Shipping Address
                </label>
                <textarea
                  id="shipping_address"
                  name="shipping_address"
                  rows={3}
                  value={formData.shipping_address}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px', fontFamily: 'inherit' }}
                  placeholder="Enter shipping address"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Billing Address
                </label>
                <textarea
                  id="billing_address"
                  name="billing_address"
                  rows={3}
                  value={formData.billing_address}
                  onChange={handleChange}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px', fontFamily: 'inherit' }}
                  placeholder="Enter billing address"
                />
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
              📝 Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              rows={3}
              value={formData.notes}
              onChange={handleChange}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px', fontFamily: 'inherit' }}
              placeholder="Additional notes about this order"
            />
          </div>

        </form>
      </div>
    </div>
  );
}

export default OrderForm;
